import { useState, useEffect, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import classNames from 'classnames';
import { AppBar } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';

import ThemeProviderWrapper from 'components/Shared/ThemeProviderWrapper';
import VehicleNotFoundDialog from 'components/Srp/VehicleNotFoundDialog';
import ProximityAd from 'components/Ad/ProximityAd';
import AdhesionAd from 'components/Ad/AdhesionAd';

import useSrpModel, { InitialSrpParams } from 'lib/hooks/useSrpModel';
import useSrpFilterStateInitialization from 'lib/hooks/useSrpFilterStateInitialization';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import useFetchLeads from 'lib/hooks/useFetchLeads';
import useAdSetup from 'lib/hooks/useAdSetup';
import { MEDIUM_RECTANGLE, HALF_PAGE_SKYSCRAPER } from 'lib/GoogleAds/Sizes';
import { Zone } from 'lib/CarGurusAds/TaxonomySchema';
import CarGurusAdUnit from 'lib/CarGurusAds/AdUnit';
import { fetchVehicle } from 'lib/AutolistAPI';
import { setSrpScrollPosition, setSrpScrolledToBottom } from 'redux-implementations/vdpScroll';

import { VehicleInfo } from 'types/Apis/VehicleApiResponse';
import State from 'types/State';

import useEvents from 'lib/hooks/useEvents';
import searchResultEvent from 'lib/Tracking/Srp/SearchResultView';
import { SourcePages } from 'lib/EventSchema';
import OnboardingBanner from 'components/Onboarding/OnboardingBanner';
import OnboardingModal from 'components/Onboarding/Modal';
import tapEvent from 'lib/Tracking/Onboarding/TapEvent';
import viewEvent from 'lib/Tracking/Onboarding/ViewEvent';
import BackToTopButton from './BackToTopButton';
import Sidebar from './Sidebar';
import ResultsList from '../Shared/ResultsList';
import { styles, useStyles } from './styles';
import DesktopSearchBar from './SearchBar/Desktop';
import MobileSearchBar from './SearchBar/Mobile';
import FilterPills from './FilterPills/FilterPills';
import ResultCount from './ResultCount';

export interface SrpProps {
  initialSearchParams?: InitialSrpParams;
  vehicleNotFound?: boolean;
}

export default function Srp(props: SrpProps): JSX.Element {
  const { isMobile } = useDeviceScreen();
  const [searchFiltersViewRef, searchFiltersInView] = useInView();
  const [vehicleDetails, setVehicleDetails] = useState<VehicleInfo[]>([]);
  const dispatch = useDispatch();
  const srpScrollPosition = useSelector<State>(
    (state) => state.vdpScroll.srpScrollPosition
  ) as number;
  const scrolledToBottom = useSelector<State>(
    (state) => state.vdpScroll.srpScrolledToBottom
  ) as boolean;
  useSrpFilterStateInitialization();
  useFetchLeads();
  const { data, isPending, hasFetchedOnce, searchVehicles } = useSrpModel({
    initialParams: props.initialSearchParams,
  });

  const logger = useEvents();
  const logSearchResultEvent = useCallback(() => {
    data.vehicles.forEach((vehicle) => {
      logger.log(
        searchResultEvent({
          trackingParams: vehicle.trackingParams,
          sourcePage: SourcePages.Srp,
        })
      );
    });
  }, [data.vehicles, logger]);

  useEffect(() => {
    logSearchResultEvent();
  }, [logSearchResultEvent]);

  useEffect(() => {
    const fetchVehicleDetails = async (): Promise<void> => {
      const details = await Promise.all(
        data.vehicles.map(async (vehicle) => fetchVehicle(vehicle.vin))
      );
      setVehicleDetails(details);
    };
    fetchVehicleDetails();
  }, [data.vehicles]);

  const carGurusAdInfo = data.adInfo;
  const classes = useStyles();

  const adUnitConfig = { zone: Zone.SRP };
  const adUnit = new CarGurusAdUnit(adUnitConfig);
  const adUnitString = (carGurusAdInfo?.adUnit as string) || adUnit.toString();

  useAdSetup(adUnitString, {
    adUnit: adUnitString,
    make: carGurusAdInfo?.make,
    model: carGurusAdInfo?.model,
    year: carGurusAdInfo?.year,
    pageType: carGurusAdInfo?.pageType,
    ageType: carGurusAdInfo?.ageType,
    cpo: carGurusAdInfo?.cpo,
    inLine: carGurusAdInfo?.inLine,
    body_style: carGurusAdInfo?.body_style,
    category: carGurusAdInfo?.category,
    app: carGurusAdInfo?.app,
  });

  useEffect(() => {
    if (scrolledToBottom) {
      setTimeout(() => window.scrollTo({ top: srpScrollPosition }), 900);
    } else {
      window.scrollTo({ top: srpScrollPosition });
    }
    dispatch(setSrpScrollPosition(null));
    dispatch(setSrpScrolledToBottom(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <ThemeProviderWrapper>
      {hasFetchedOnce && (
        <VehicleNotFoundDialog
          listingsCount={data.totalCount}
          isInitiallyShown={props.vehicleNotFound}
        />
      )}
      <div className="srp-outer">
        <BackToTopButton />

        <AppBar position="sticky" className={classes.appBar} elevation={0}>
          <MobileSearchBar totalCount={data.totalCount} searchVehicles={searchVehicles} />
        </AppBar>
        <div className="srp-inner">
          <div className="srp-sidebar">
            <div ref={searchFiltersViewRef}>
              <Sidebar />
            </div>
            <ProximityAd
              adConfig={{
                targeting: { inLine: 2 },
                dimensions: [[300, 250]],
              }}
              placeholderSize={MEDIUM_RECTANGLE}
              className={`sidebar-ad`}
            />
            <div
              className={classNames('sidebar-ads', { 'sticky-sidebar-ads': !searchFiltersInView })}
            >
              <ProximityAd
                adConfig={{
                  targeting: { inLine: 3 },
                  dimensions: [[300, 250]],
                }}
                placeholderSize={MEDIUM_RECTANGLE}
                className={`sidebar-ad`}
              />
              <ProximityAd
                adConfig={{
                  targeting: { inLine: 4 },
                  dimensions: [[300, 600]],
                }}
                placeholderSize={HALF_PAGE_SKYSCRAPER}
                className={`sidebar-ad`}
              />
            </div>
          </div>
          <div className="srp-right-container">
            <DesktopSearchBar totalCount={data.totalCount} searchVehicles={searchVehicles} />
            <div className="filters-desktop">
              <ResultCount totalCount={data.totalCount} isLoading={isPending} />
              <FilterPills />
            </div>
            {
              <OnboardingBanner
                onClick={(): void => {
                  setIsModalOpen(true);
                  logger.log(tapEvent({ action: 'onboarding_start', sourcePage: SourcePages.Srp }));
                  logger.log(viewEvent({ sourcePage: SourcePages.OnboardingWelcome }));
                }}
              />
            }
            <OnboardingModal
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              isPending={isPending}
            />
            <ResultsList
              vehicles={data.vehicles}
              totalCount={data.totalCount}
              isLoading={isPending}
              vehicleDetails={vehicleDetails}
              searchVehicles={searchVehicles}
            />
          </div>
        </div>
        {isMobile && <AdhesionAd adhereToBottom={true} targeting={11} />}
      </div>
      <style jsx>{styles}</style>
    </ThemeProviderWrapper>
  );
}
